import { translateError } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { Download, Link2, Loader2, ArrowRight, ShieldCheck, Film } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { API } from "@/config";
import { prepareVideo, getVideoJob } from "@/lib/api";

type Phase = "idle" | "preparing" | "ready" | "error";

// "Convert": public video URL -> MP4 (logic unchanged; UI only).
export default function SimulationStudios() {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  };
  useEffect(() => stopPolling, []);

  const start = async () => {
    stopPolling();
    setError(null);
    setJobId(null);
    if (!url.trim()) {
      setPhase("error");
      setError("Inserisci un URL.");
      return;
    }
    setPhase("preparing");
    try {
      const { job_id } = await prepareVideo(url.trim());
      timer.current = setInterval(async () => {
        try {
          const job = await getVideoJob(job_id);
          if (job.status === "READY") {
            stopPolling();
            setJobId(job_id);
            setPhase("ready");
          } else if (job.status === "FAILED") {
            stopPolling();
            setPhase("error");
            setError(translateError(job.error, "convert.prepareFailed"));
          }
        } catch {
          stopPolling();
          setPhase("error");
          setError(t("convert.prepareFailed"));
        }
      }, 2000);
    } catch (e: any) {
      setPhase("error");
      setError(
        translateError(e?.response?.data?.detail) || t("convert.prepareFailed"),
      );
    }
  };

  return (
    <Layout>
        <main className="relative mx-auto max-w-4xl px-6 pb-32 pt-16 sm:px-10 lg:pt-20" data-testid="simulation-studios-page">
          <span className="eyebrow">{t("convert.eyebrow")}</span>
          <h1 className="headline-shadow mt-5 font-display text-4xl font-bold uppercase leading-[0.98] tracking-wide text-foreground sm:text-5xl lg:text-6xl">
            <span className="text-neon">Convert</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm text-[#B8C4DA] sm:text-base">
            {t("convert.subtitle")}
          </p>

          <div className="glass brackets relative mt-10 rounded-2xl p-6 sm:p-8">
            <span className="bk" />
            <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-[#7FD4FF]/70 to-transparent" />
            <label htmlFor="video-url" className="block text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">
              {t("convert.urlLabel")}
            </label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <div className="relative flex-1">
                <Link2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7FD4FF]/70" strokeWidth={1.75} />
                <input
                  id="video-url"
                  name="video-url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && phase !== "preparing") start();
                  }}
                  placeholder="https://…"
                  autoComplete="off"
                  data-testid="simulation-url-input"
                  className="field-premium h-12 pl-11 pr-4 text-sm sm:text-base"
                />
              </div>
              <button
                type="button"
                onClick={start}
                disabled={phase === "preparing"}
                data-testid="simulation-submit-btn"
                className="cta-hero !h-12 !px-7 text-sm"
              >
                <span className="inline-flex items-center gap-2">
                  {phase === "preparing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />}
                  Convert
                  {phase !== "preparing" && <ArrowRight className="h-4 w-4" />}
                </span>
              </button>
            </div>
            <p className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 text-[#7FD4FF]" strokeWidth={1.75} />
              {t("convert.publicOnly")}
            </p>

            {phase === "preparing" && (
              <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#2E7BFF]/40 bg-[#2E7BFF]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#7FD4FF]" data-testid="simulation-status">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#7FD4FF] shadow-[0_0_8px_#7FD4FF]" />
                {t("convert.preparing")}
              </p>
            )}

            {phase === "error" && error && (
              <p
                className="mt-6 rounded-lg border border-[#FF4D4D]/40 bg-[#FF4D4D]/10 px-4 py-3 text-sm text-[#FF8A8A] shadow-[0_0_30px_-16px_rgba(255,77,77,0.6)]"
                data-testid="simulation-error"
              >
                {error}
              </p>
            )}
          </div>

          {phase === "ready" && jobId && (
            <div className="mt-8 space-y-5" data-testid="simulation-result">
              <div className="relative rounded-2xl border border-[#4d8fff]/50 bg-[#070B14] p-[6px] shadow-[0_0_0_1px_rgba(46,123,255,0.25),0_0_60px_-12px_rgba(46,123,255,0.7),0_40px_90px_-40px_rgba(0,0,0,0.95)]">
                <div aria-hidden className="absolute inset-x-12 -top-px h-px bg-gradient-to-r from-transparent via-[#9CC8FF] to-transparent" />
                <video
                  key={jobId}
                  src={`${API}/simulation-studios/jobs/${jobId}/video`}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full rounded-xl bg-black"
                  data-testid="simulation-player"
                />
              </div>
              <a
                href={`${API}/simulation-studios/jobs/${jobId}/video?download=true`}
                className="inline-flex"
                data-testid="simulation-download-link"
              >
                <Button variant="secondary" data-testid="simulation-download-btn">
                  <Download />
                  {t("common.downloadMp4")}
                </Button>
              </a>
            </div>
          )}
        </main>
    </Layout>
  );
}
